<template>
    <div class="promo-actions">
        <h3
            v-text="'Akcje'"
            class="typo typo--headline"
        />

        <the-btn
            v-if="activateBtnVisible"
            v-tooltip="activationBtnText"
            id="single-promo-activate"
            btn-class="btn-block btn-outline-custom"
            text="Aktywuj promocję"
            icon="Promotions"
            :disabled="allSubsRejectedToday"
            @click="activatePromo"
        />

        <the-btn
            v-if="canGenerateLabels"
            btn-class="btn-block btn-outline-custom"
            text="Generuj cenówki"
            mobile-text="Cenówki"
            icon="PriceTags"
            @click="generateLabels"
        />

        <the-btn
            v-if="canCopyPromo"
            btn-class="btn-block btn-outline-custom"
            text="Utwórz ponownie"
            :icon="copyLoader ? 'Spinner' : 'Copy'"
            @click="copyPromo"
        />

        <the-btn
            v-if="promotion.happy_hours"
            class="btn-block btn-outline-custom"
            text="Podgląd Happy Hours"
            icon="Promo"
            @click="openHappyHoursModal"
        />

        <the-btn
            v-if="hasTerms"
            class="btn-block btn-outline-custom"
            text="Pobierz warunki promocji"
            icon="Download"
            @click="downloadTerms"
        />

        <the-btn
            v-if="hasAttachments"
            class="btn-block btn-outline-custom"
            text="Pobierz materiały promocyjne"
            icon="Download"
            @click="onAttachmentsDownload"
        />

        <the-btn
            v-if="canAbortPromo"
            class="btn-block btn-danger"
            text="Przerwij promocję"
            icon="Cancel"
            @click="abortPromo"
        />

        <v-popover
            v-if="canRemovePromo"
            class="w-100"
            offset="8"
        >
            <the-btn
                class="btn-block btn-danger"
                text="Usuń promocję"
                icon="Bin"
            />
            <template slot="popover">
                <popover-confirm
                    confirm-btn-id="cancel-promos-btn"
                    @confirmed="cancelPromo"
                />
            </template>
        </v-popover>
    </div>
</template>

<script>
export default {
    props: {
        promotion: {
            type: Object,
            required: true
        },

        deselectAll: {
            type: Function,
            required: true
        },

        acceptPromos: {
            type: Function,
            required: true
        },

        isLocked: {
            type: Boolean,
            default: false
        }
    },

    data: () => ({
        copyLoader: false
    }),

    computed: {
        merchants() {
            return this.$store.getters.getMerchants
        },

        isPromoEditPermission() {
            return this.$store.getters.isPromoEditPermission
        },

        isSuspended() {
            return this.promotion.subscriptions.every(
                el => el.suspended === true
            )
        },

        allSubsRejectedToday() {
            const today = this.$moment()
            return this.subscriptions.every(subscription => {
                const updatedDate = this.$moment(subscription.updated_at)

                return (
                    subscription.state === 'interrupted' &&
                    today.isSame(updatedDate, 'day')
                )
            })
        },

        activateBtnVisible() {
            if (this.isLocked || !this.isPromoEditPermission) {
                return false
            }

            return this.isActivationAllowed || this.allSubsRejectedToday
        },

        canGenerateLabels() {
            const { state, type } = this.promotion

            const isPromoActive = state === 'active' && !this.isSuspended
            const isLabelTypeValid = this.$store.getters.isLabelType(type)
            const hasAcceptedSubscriptions = this.subscriptions.some(sub => sub.state === 'accepted')

            return isPromoActive && isLabelTypeValid && hasAcceptedSubscriptions
        },

        canAbortPromo() {
            const canEditPromo = !this.isSuspended && this.isPromoEditPermission
            const hasSubscriptionsToStop = this.subscriptionsToStop.length > 0

            return canEditPromo && hasSubscriptionsToStop
        },

        canRemovePromo() {
            const canEditPromo = !this.isSuspended && this.isPromoEditPermission
            const isRemovablePromo = this.isRetailersPromo && this.isOwnPromoPermission
            const isNotFinished = this.promotion.state !== 'finished'

            return canEditPromo && isRemovablePromo && isNotFinished
        },

        canCopyPromo() {
            const canEditPromo = !this.isSuspended && this.isPromoEditPermission
            const isRemovablePromo = this.isRetailersPromo && this.isOwnPromoPermission

            return canEditPromo && isRemovablePromo
        },

        // Subscriptions filtered by active merchants
        subscriptions() {
            const activeMerchantIds = this.merchants.map(el => el.id)

            return this.promotion.subscriptions.filter(el =>
                activeMerchantIds.includes(el.merchant_id)
            )
        },

        isActivationAllowed() {
            if (this.promotion.state === 'finished' || this.isSuspended) {
                return false
            }

            return this.subscriptions.some(
                el => el.state === 'available'
            )
        },

        activationBtnText() {
            if (this.subscriptions.length === 0) {
                return ''
            }

            const statuses = this.subscriptions.map(sub => sub.state)

            if (statuses.every(status => status === 'conflict')) {
                // Promo is in conflict with other promos, cannot activate
                return 'Nie możesz aktywować tej promocji ze względu na konflikty'
            }

            if (statuses.includes('available')) {
                // Promo is able to be activated
                return 'Aktywuj tę promocję we wszystkich sklepach'
            } else if (statuses.includes('interrupted')) {
                // Promo is interrupted, cannot activate today
                return 'Promocja została przerwana. Ponowna aktywacja promocji będzie możliwa jutro'
            } else if (statuses.includes('accepted')) {
                // Promo is already activated
                return 'Ta promocja już jest aktywna'
            }

            return ''
        },

        isOwnPromo() {
            return (
                +this.promotion.promo_group_id ===
                +process.env.VUE_APP_OWN_GROUP_ID
            )
        },

        isOwnPromoPermission() {
            return this.$store.getters.isOwnPromoPermission
        },

        promoGroup() {
            const group = this.$store.getters.getPromoGroups[0]
            return {
                ...group,
                selectedPromoIds: [this.promotion.id]
            }
        },

        subscriptionsToStop() {
            return this.$store.getters.getSubscriptionsToStop
        },

        isRetailersPromo() {
            const retailerCompany = this.$store.getters.getRetailersCompanies[0]
            return this.promotion.company_id === retailerCompany?.id
        },

        hasTerms() {
            return ((this.promotion.terms || {}).urls || {}).original
        },

        hasAttachments() {
            return ((this.promotion.attachment || {}).urls || {}).original
        },

        hasProductSets() {
            return this.$store.getters.hasProductSets(this.promotion.type)
        }
    },

    methods: {
        async activatePromo() {
            if (!this.isActivationAllowed) {
                return
            }

            const acceptPromo = async () => {
                await this.selectPromoGroupPromo()

                const group = this.$store.getters.getSelectedPromoGroups[0]
                if (!group.merchantIds) {
                    await this.$store.dispatch('downloadGroupMerchants', group)
                }

                this.$store.dispatch('selectAllMerchants', {
                    promoId: this.promotion.id,
                    shops: this.promotion.subscriptions || []
                        .filter(el => el.state === 'available')
                        .map(el => el.merchant_id),
                    value: true,
                    promotion: this.promotion,
                    fromPromoDetails: true
                })

                this.acceptPromos()
            }

            await this.deselectAll()

            if (this.promoGroup.acceptance === 'any') {
                await acceptPromo()
            } else {
                const confirm = {
                    message: `Promocja należy do grupy zamkniętej. Aktywowanie tej promocji będzie skutkowało aktywowaniem całej grupy <strong>${this.promoGroup.name}</strong>. Czy chcesz aktywować? `,
                    header: `Aktywowanie promocji <h3 class="h5 px-2 pt-2">${this.promotion.name}</h3>`,
                    abortButtonText: 'Wróć',
                    confirmButtonText: 'Potwierdzam',
                    abortButtonCallback: () => {
                        this.$store.dispatch('toggleModal', false)
                    },
                    confirmButtonCallback: async () => {
                        await acceptPromo()
                    }
                }
                this.$store.dispatch('openConfirmationWindow', confirm)
            }
        },

        generateLabels() {
            if (this.canGenerateLabels) {
                this.$store.dispatch('openModal', {
                    component: 'GenerateLabels',
                    args: {
                        groups: [this.promoGroup]
                    }
                })
            }
        },

        async cancelPromo() {
            const promoId = this.promotion.id

            const { status } = await this.$store.dispatch('cancelPromotion', {
                promoId
            })

            if (status !== 200) {
                return
            }

            this.$store.dispatch('loadPromotion', { promoId })
        },

        downloadTerms() {
            if (!this.hasTerms) {
                return
            }

            this.$functions.downloadFile(
                this.promotion.terms.urls.original,
                this.promotion.terms.name ||
                    `WarunkiPromocji_${this.promotion.id}`
            )
        },

        onAttachmentsDownload() {
            if (!this.hasAttachments) {
                return
            }

            this.$store.dispatch('fetchPromotionAttachments', {
                id: this.promotion.id,
                name: this.promotion.name,
                merchantNetworkId: [
                    ...new Set(
                        this.promotion.subscriptions.map(
                            e => e.merchant_network_id
                        )
                    )
                ]
            })
        },

        abortPromo() {
            if (!this.canAbortPromo) {
                return
            }

            const selectPromo = async () => {
                await this.selectPromoGroupPromo({
                    'subscription|state': 'accepted'
                })
                this.$store.dispatch('openModal', {
                    component: 'StopPromotion'
                })
            }
            if (this.promoGroup.acceptance === 'any') {
                selectPromo()
            } else {
                const confirm = {
                    message: `Promocja należy do grupy zamkniętej. Przerwanie tej promocji będzie skutkowało anulowaniem całej grupy <strong>${this.promoGroup.name}</strong>. Czy chcesz przerwać? `,
                    header: `Przerwanie promocji <h3 class="h5 px-2 pt-2">${this.promotion.name}</h3>`,
                    abortButtonText: 'Wróć',
                    confirmButtonText: 'Potwierdzam',
                    abortButtonCallback: () => {
                        this.$store.dispatch('toggleModal', false)
                    },
                    confirmButtonCallback: () => {
                        selectPromo()
                    }
                }
                this.$store.dispatch('openConfirmationWindow', confirm)
            }
        },

        selectPromoGroupPromo(params) {
            return this.$store.dispatch('selectPromoGroupPromo', {
                promoId: this.promotion.id,
                groupId: this.promotion.groupId,
                select: true,
                acceptance: this.promoGroup.acceptance,
                addPromoWholeGroup: true,
                fromPromoDetails: true,
                params
            })
        },

        openHappyHoursModal() {
            this.$store.dispatch('openModal', {
                component: 'HappyHours'
            })
        },

        async copyPromo() {
            if (!this.canCopyPromo) {
                return
            }

            this.copyLoader = true

            try {
                // Fetch subscriptions products / product sets
                await this.fetchSubscriptionsProducts()

                // Check if products changed product type
                const res = await this.checkProductsTypes()

                if (res.status !== 200) {
                    throw new Error('Failed to check products types')
                }

                await this.$store.dispatch('setChangedProducts', res.changedProducts)

                const changedProductIds = (res.changedProducts || []).map(
                    product => product.dict_product.id
                )

                await this.$store.dispatch('copyPromo', {
                    promotion: this.promotion,
                    changedProductIds
                })
                this.$router.push({ name: 'add-new-promo' })
            } finally {
                this.copyLoader = false
            }
        },

        async fetchSubscriptionsProducts() {
            const subscriptionIds = this.subscriptions.map(sub => sub.id).join(',')
            if (this.hasProductSets) {
                const sets = await this.$store.dispatch('fetchSetSubscriptions', {
                    includes: 'product_set',
                    subscription_id: subscriptionIds
                })
                const products = await this.$store.dispatch('fetchProducts', {
                    params: {
                        includes: 'dict_product,dict_product.image',
                        promo_id: this.promotion.id,
                        per: 99999
                    }
                })

                for (const set of sets) {
                    set.product_set.dict_products = products.data.data.filter(
                        product => product.product_set_id === set.product_set.id
                    )
                }

                for (const subscription of this.promotion.subscriptions) {
                    subscription.product_set_subscriptions = sets.filter(
                        set => set.subscription_id === subscription.id
                    )
                }
            } else {
                const products = await this.$store.dispatch('fetchProductSubscriptions', {
                    params: {
                        subscription_id: subscriptionIds,
                        includes: 'promo_dict_product,promo_dict_product.dict_product,dict_product.image',
                        per: 99999
                    }
                })
                for (const subscription of this.promotion.subscriptions) {
                    subscription.product_subscriptions = products.filter(
                        product => product.subscription_id === subscription.id
                    )
                }
            }
        },

        async checkProductsTypes() {
            try {
                let promoProducts
                if (this.hasProductSets) {
                    promoProducts = this.$store.getters.getPromoProductSets.flatMap(e => e.product_set_dict_products)
                } else {
                    promoProducts = this.$store.getters.getPromoProducts
                }

                // Fetch products
                const { status, data } = await this.$store.$api.get('dict_products', {
                    params: {
                        id: promoProducts.map(product => product.dict_product.id).join(',')
                    }
                })

                if (status !== 200) {
                    throw new Error('Failed to fetch products')
                }

                const resProducts = data.data

                // Check if promotion is available in weight or piece
                const availableInPromos = this.$store.getters.isWeight(this.promotion.type)
                    ? 'weight'
                    : 'piece'

                // Check if products changed product type
                const changedProducts = promoProducts.filter(product => {
                    const resProduct = resProducts.find(
                        resProduct => resProduct.id === product.dict_product.id
                    )

                    return resProduct && resProduct.available_in_promos !== availableInPromos
                })

                return {
                    status,
                    changedProducts
                }
            } catch (error) {
                console.error(error)
            }
        }
    }
}
</script>

<style lang="scss">
.promo-actions {
    h3 {
        margin-bottom: 1rem;
        ~ * {
            margin-top: 0.5rem;
        }
    }
}
</style>